import {Component, Fragment} from 'react'
import PropTypes from 'prop-types'
import {toastr} from 'react-redux-toastr'

import {handleResponseErrors} from '../../services/processServerErrors.js'
import scrollToElement from '../../services/scrollTo.js'
import api from '../../../api.js'
import Loader from '../../../shared/components/Loader/Loader.jsx'
import connectModal from '../../../shared/components/Modals/connect.js'
import ClearanceListItem from '../../../shared/components/Clearance/ClearanceListItem.jsx'
import {clearanceTypes, clearanceStatuses, defenseLevels, defenseScopes} from '../../../shared/services/clearanceConstants.js'

import ClearanceForm from '../Forms/ClearanceForm/ClearanceForm.jsx'
import DeleteClearanceModal from '../Modals/Clearance/DeleteClearanceModal.jsx'

/**
 * Saisie et liste des habilitations d'un freelance : utilisable aussi bien depuis la fiche
 * profil que depuis le tunnel d'inscription.
 */
export class ClearanceManager extends Component {
  static propTypes = {
    freelanceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    emptyText: PropTypes.node,
  }

  constructor(props) {
    super(props)
    this.state = {
      loading: false,
      clearances: [],
      selectedType: clearanceTypes.defense.value,
      editing: null,
      file: null,
    }
  }

  componentDidMount() {
    this.fetchClearances()
  }

  fetchClearances = async () => {
    this.setState({loading: true})
    try {
      const response = await api.freelanceClearances.getAll(null, {freelance: this.props.freelanceId})
      this.setState({clearances: response.data})
    } catch (e) {
      console.warn(e)
    } finally {
      this.setState({loading: false})
    }
  }

  buildPayload = (values) => {
    const payload = {
      status: values.status,
      obtainedAt: this.formatDate(values.obtainedAt),
      expiresAt: this.formatDate(values.expiresAt),
      issuingAuthority: values.issuingAuthority || null,
      nationalityRestriction: values.nationalityRestriction || null,
    }

    switch (this.state.selectedType) {
    case clearanceTypes.defense.value:
      payload.level = values.level
      payload.scope = values.scope
      break
    case clearanceTypes.radiation_protection.value:
      payload.category = values.category
      break
    case clearanceTypes.site_access.value:
      payload.operatingSite = values.operatingSite
      break
    }

    return payload
  }

  formatDate = (value) => {
    return value ? value.split(' ')[0] : null
  }

  initialValuesFor = (clearance) => ({
    status: clearance.status,
    obtainedAt: clearance.obtained_at,
    expiresAt: clearance.expires_at,
    issuingAuthority: clearance.issuing_authority,
    nationalityRestriction: clearance.nationality_restriction,
    level: clearance.level,
    scope: clearance.scope,
    category: clearance.category,
    operatingSite: clearance.operating_site,
  })

  // Les selects Niveau/Périmètre n'ont pas d'option vide : le navigateur affiche toujours
  // la première valeur, il faut donc l'initialiser explicitement pour que l'état du formulaire
  // corresponde à ce qui est affiché (sinon la validation les considère comme non renseignés).
  defaultValuesFor = (type) => {
    switch (type) {
    case clearanceTypes.defense.value:
      return {level: Object.values(defenseLevels)[0].value, scope: Object.values(defenseScopes)[0].value}
    default:
      return {}
    }
  }

  upsertClearance = (clearance) => {
    this.setState((state) => {
      const exists = state.clearances.some((c) => c.id === clearance.id)
      return {
        clearances: exists
          ? state.clearances.map((c) => (c.id === clearance.id ? clearance : c))
          : [...state.clearances, clearance]
      }
    })
  }

  handleTypeChange = (type) => {
    this.setState({selectedType: type})
  }

  handleFileChange = (file) => {
    this.setState({file})
  }

  handleEdit = (clearance) => {
    this.setState({editing: clearance, selectedType: clearance.type, file: null})
    scrollToElement('#ClearanceForm')
  }

  handleCancelEdit = () => {
    this.setState({editing: null, file: null, selectedType: clearanceTypes.defense.value})
  }

  handleSubmit = async (values) => {
    const {freelanceId} = this.props
    const payload = this.buildPayload(values)
    const editingClearance = this.state.editing

    let clearance
    try {
      const response = editingClearance
        ? await api.freelanceClearances.edit(payload, {freelance: freelanceId, clearance: editingClearance.id})
        : await api.freelanceClearances.add(payload, {freelance: freelanceId, type: this.state.selectedType})
      clearance = response.data
    } catch (e) {
      return handleResponseErrors(e)
    }

    this.upsertClearance(clearance)
    this.setState({editing: null})

    if (this.state.file) {
      const pendingFile = this.state.file
      this.setState({file: null})

      try {
        const formData = new FormData()
        formData.append('uploadedFile', pendingFile, pendingFile.name)
        const documentResponse = await api.freelanceClearances.document.upload(
          {freelance: freelanceId, clearance: clearance.id},
          {body: formData}
        )
        this.upsertClearance(documentResponse.data)
      } catch (e) {
        console.warn(e)
        toastr.error('Le justificatif n\'a pas pu être envoyé, vous pouvez réessayer en éditant l\'habilitation.')
      }
    }

    return clearance
  }

  handleDelete = (clearance) => {
    this.props.openDeleteClearanceModal({
      clearance,
      onSubmit: this.deleteClearance,
    })
  }

  deleteClearance = async (clearance) => {
    const {freelanceId} = this.props
    try {
      await api.freelanceClearances.remove(null, {freelance: freelanceId, clearance: clearance.id})
      this.setState((state) => ({
        clearances: state.clearances.filter((c) => c.id !== clearance.id)
      }))
    } catch (e) {
      console.warn(e)
    }
  }

  render() {
    const {freelanceId, emptyText} = this.props
    const {clearances, loading, selectedType, editing, file} = this.state
    const hasClearances = clearances.length > 0

    return (
      <Fragment>
        <ClearanceForm
          initialValues={editing
            ? this.initialValuesFor(editing)
            : {status: clearanceStatuses.valid.value, ...this.defaultValuesFor(selectedType)}}
          selectedType={selectedType}
          editing={editing}
          onTypeChange={this.handleTypeChange}
          onCancel={this.handleCancelEdit}
          file={file}
          onFileChange={this.handleFileChange}
          onSubmit={this.handleSubmit}
          existingDocument={editing?.supporting_document || null}
          existingDocumentUrl={
            editing?.supporting_document
              ? `${process.env.API_URL}/freelances/${freelanceId}/clearances/${editing.id}/document`
              : null
          }
        />

        <h4 className="text-center mt-5 mb-4">Habilitations renseignées</h4>

        {!hasClearances
          ? <div className="callout callout-secondary text-center col-md-7 mx-auto">
            <p>
              {emptyText ?? <Fragment>
                Aucune habilitation n&apos;a encore été renseignée pour ce consultant.
                Utilisez le formulaire ci-dessus pour en ajouter une.
              </Fragment>}
            </p>
          </div>
          : clearances.map((clearance) => (
            <ClearanceListItem
              key={`clearance-${clearance.id}`}
              clearance={clearance}
              freelanceId={freelanceId}
              onEdit={this.handleEdit}
              onDelete={this.handleDelete}
            />
          ))
        }

        <Loader visible={loading}>
          Chargement...
        </Loader>
      </Fragment>
    )
  }
}

export default connectModal({DeleteClearanceModal}, ClearanceManager)
